import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, Route, Routes } from 'react-router-dom';
import {
    FacebookShareButton,
    TwitterShareButton,
    FacebookIcon,
    TwitterIcon
} from 'react-share';
import {
    Video,
    Headphones,
    ChevronLeft,
    ChevronRight,
    Navigation,
    ChevronUp,
    ChevronDown,
    FileText,
    User,
    Share2,
    MapPin,
    Calendar
} from 'lucide-react';
import {
    getAuctioneerUrl,
    ensureProtocol,
    getAuctionLightboxUrl
} from '../../utils/urls';
import { auctionApiPhotosToGridImages } from '../../utils/photos';
import {
    AUCTION_PROP_TYPE,
    PHOTO_PROP_TYPE
} from '../../utils/props';
import DateTimeRange from '../DateTimeRange/DateTimeRange';
import ProgressIndicator from '../ProgressIndicator/ProgressIndicator';
import BidNowButton from '../BidNowButton';
import GuidePrice from '../GuidePrice';
import AuctionCategories from '../AuctionCategories';
import AuctionImage from '../AuctionImage';
import AuctionLightbox from '../../containers/AuctionLightbox';
import {
    getGoogleMapsUrl,
    truncate
} from '../../utils/strings';
import {
    getAuctionCityState,
    getAuctioneerCityState,
    getFullAuctionAddress,
    getAuctioneerDesignations,
} from '../../utils/auctions';
import {
    getFeature,
    FEATURE__HIGHLIGHT_PREMIUM,
    FEATURE__BROWSER_ROUTER
} from '../../utils/features';

import './AuctionPage.css';
import { RealEstateDetails } from './RealEstateDetails';
import { RealEstateCharacteristics } from './RealEstateCharacteristics';
import EnergyRating from '../EnergyRating/EnergyRating';
import PropertyDetails from '../PropertyDetails/PropertyDetails';

var allowSharing = function() {
    return getFeature(FEATURE__BROWSER_ROUTER);
};

/* ===========================================
   Sub-components
   =========================================== */

var SharePanel = function(props) {
    var show = props.show;
    var title = props.title;
    var description = props.description;
    var picture = props.picture;

    if (!show) return null;

    var url = window.location.href;
    var iconSize = 32;
    var twitterTitle = truncate(title, 139);

    return (
        <div className="al-share-panel">
            <FacebookShareButton
                url={url}
                title={title}
                description={truncate(description, 300)}
                picture={picture}
            >
                <FacebookIcon size={iconSize} round />
            </FacebookShareButton>
            <TwitterShareButton
                url={url}
                title={twitterTitle}
            >
                <TwitterIcon size={iconSize} round />
            </TwitterShareButton>
        </div>
    );
};

var VideoButton = function(props) {
    var url = props.url;
    var hasPremium = props.hasPremium;

    if (!url) return null;

    var className = 'al-media-button al-video-button' + (hasPremium ? ' al-media-button--below-premium' : '');

    return (
        <a
            href={ensureProtocol(url)}
            className={className}
            title="Watch video"
            target="_blank"
            rel="noopener noreferrer"
        >
            <Video size={22} />
        </a>
    );
};

var AudioTourButton = function(props) {
    var url = props.url;

    if (!url) return null;

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title="Interactive Language Tour"
            className="al-action-button al-audiotour-button"
        >
            <Headphones size={18} />
            <span>Interactive Tour</span>
        </a>
    );
};

var HeroGallery = function(props) {
    var photos = props.photos;
    var auctionId = props.auctionId;
    var videoUrl = props.videoUrl;
    var audioTourUrl = props.audioTourUrl;
    var hasPremium = props.hasPremium;

    var indexState = useState(0);
    var currentIndex = indexState[0];
    var setCurrentIndex = indexState[1];

    // Preload adjacent images for smooth transitions
    useEffect(function() {
        if (!photos || photos.length <= 1) return;

        var prevIndex = currentIndex === 0 ? photos.length - 1 : currentIndex - 1;
        var nextIndex = currentIndex === photos.length - 1 ? 0 : currentIndex + 1;

        var preloadUrls = [
            photos[prevIndex] && photos[prevIndex].url,
            photos[nextIndex] && photos[nextIndex].url
        ].filter(Boolean);

        preloadUrls.forEach(function(url) {
            var img = new Image();
            img.src = url;
        });
    }, [currentIndex, photos]);

    if (!photos || photos.length === 0) return null;

    var currentPhoto = photos[currentIndex];
    var hasMultiple = photos.length > 1;
    var currentPhotoId = currentPhoto && currentPhoto.id;

    var goToPrev = function(e) {
        e.preventDefault();
        e.stopPropagation();
        setCurrentIndex(function(prev) {
            return prev === 0 ? photos.length - 1 : prev - 1;
        });
    };

    var goToNext = function(e) {
        e.preventDefault();
        e.stopPropagation();
        setCurrentIndex(function(prev) {
            return prev === photos.length - 1 ? 0 : prev + 1;
        });
    };

    return (
        <div className="al-hero-gallery">
            <AuctionImage
                photo={currentPhoto}
                link={getAuctionLightboxUrl(auctionId, currentPhotoId)}
                useDefaultStyling={true}
            >
                <VideoButton url={videoUrl} hasPremium={hasPremium} />
                <AudioTourButton url={audioTourUrl} />
            </AuctionImage>

            {hasMultiple && (
                <React.Fragment>
                    <button
                        className="al-gallery-nav al-gallery-prev"
                        onClick={goToPrev}
                        type="button"
                        aria-label="Previous photo"
                    >
                        <ChevronLeft size={32} />
                    </button>
                    <button
                        className="al-gallery-nav al-gallery-next"
                        onClick={goToNext}
                        type="button"
                        aria-label="Next photo"
                    >
                        <ChevronRight size={32} />
                    </button>
                    <div className="al-gallery-counter">
                        {currentIndex + 1} / {photos.length}
                    </div>
                </React.Fragment>
            )}
        </div>
    );
};

var PhotoGrid = function(props) {
    var photos = props.photos;
    var auctionId = props.auctionId;
    var title = props.title;

    if (!photos || photos.length === 0) return null;

    var photoElements = auctionApiPhotosToGridImages(photos, auctionId, title);

    return (
        <div className="al-photo-grid">
            {photoElements}
        </div>
    );
};

var DirectionsSection = function(props) {
    var directions = props.directions;
    var googleMapsUrl = props.googleMapsUrl;

    if (!directions && !googleMapsUrl) return null;

    return (
        <section className="al-section al-directions-section">
            {directions && (
                <React.Fragment>
                    <h3 className="al-section-title">Directions</h3>
                    <div className="al-directions-text">{directions}</div>
                </React.Fragment>
            )}
            {googleMapsUrl && (
                <a
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="al-action-button al-directions-button"
                >
                    <Navigation size={18} />
                    <span>Get Directions</span>
                </a>
            )}
        </section>
    );
};

var TermsSection = function(props) {
    var terms = props.terms;
    var expandedState = useState(false);
    var expanded = expandedState[0];
    var setExpanded = expandedState[1];

    if (!terms) return null;

    var sectionClass = 'al-section al-terms-section' + (expanded ? ' al-terms-expanded' : '');

    return (
        <section className={sectionClass}>
            <button
                className="al-terms-toggle"
                onClick={function() { setExpanded(!expanded); }}
                type="button"
            >
                <span>Terms</span>
                {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            <div className="al-terms-content">
                <pre>{terms}</pre>
            </div>
        </section>
    );
};

var ActionBar = function(props) {
    var auction = props.auction;
    var auctioneerUrl = props.auctioneerUrl;

    if (!auction) return null;

    return (
        <div className="al-action-bar">
            {auction.companyWebsite && (
                <a
                    href={ensureProtocol(auction.companyWebsite)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="al-action-button"
                >
                    <FileText size={18} />
                    <span>Auction Brochure</span>
                </a>
            )}
            <Link to={auctioneerUrl} className="al-action-button">
                <User size={18} />
                <span>About {auction.auctioneerName}</span>
            </Link>
        </div>
    );
};

/* ===========================================
   Main Component
   =========================================== */

var AuctionPage = function(props) {
    var match = props.match;
    var auction = props.auction;
    var mainPhoto = props.mainPhoto;
    var secondaryPhotos = props.secondaryPhotos;
    var overflowPhotos = props.overflowPhotos;
    var onInitialize = props.onInitialize;

    var shareState = useState(false);
    var showShare = shareState[0];
    var setShowShare = shareState[1];

    var auctionId = match && match.params && match.params.auctionId;

    // Load auction data when auctionId changes
    useEffect(function() {
        // Scroll to top when navigating to this page
        window.scrollTo(0, 0);

        if (onInitialize) {
            onInitialize();
        }
    }, [auctionId]); // Re-run when navigating to different auction

    // Show loading if no auction OR if auction ID doesn't match URL (stale data)
    var isLoading = !auction || String(auction.id) !== String(auctionId);

    if (isLoading) {
        return (
            <div className="al-auction-page al-auction-loading">
                <ProgressIndicator text="Loading auction details..." />
            </div>
        );
    }

    var premium = getFeature(FEATURE__HIGHLIGHT_PREMIUM) && auction.premium;
    var auctioneerUrl = getAuctioneerUrl(auction.userId);
    var cityState = getAuctionCityState(auction);
    var auctioneerLocation = getAuctioneerCityState(auction);
    var designations = getAuctioneerDesignations(auction);
    var googleMapsUrl = getGoogleMapsUrl(getFullAuctionAddress(auction));
    var mainPhotoUrl = mainPhoto && mainPhoto.url;

    // Combine all photos for gallery
    var allPhotos = [mainPhoto]
        .concat(secondaryPhotos || [])
        .concat(overflowPhotos || [])
        .filter(Boolean);

    return (
        <article className="al-auction-page">
            {/* Premium Badge */}
            {premium && (
                <div className="al-premium-badge">Premium</div>
            )}

            {/* Hero Gallery */}
            <div className="al-hero">
                <HeroGallery
                    photos={allPhotos}
                    auctionId={auction.id}
                    videoUrl={auction.videoUrl}
                    audioTourUrl={auction.audioTourUrl}
                    hasPremium={premium}
                />

                {/* Share Button */}
                {allowSharing() && (
                    <div className="al-share-wrapper">
                        <button
                            className="al-share-button"
                            onClick={function() { setShowShare(!showShare); }}
                            type="button"
                            aria-label="Share"
                        >
                            <Share2 size={20} />
                        </button>
                        <SharePanel
                            show={showShare}
                            title={auction.title}
                            description={auction.description}
                            picture={mainPhotoUrl}
                        />
                    </div>
                )}
            </div>

            {/* Lightbox Route */}
            <Routes>
                <Route path="photo/:photoId" element={<AuctionLightbox />} />
            </Routes>

            {/* Title & Bid */}
            <header className="al-auction-header">
                <h1 className="al-auction-title">{auction.title}</h1>
                <div className="al-auction-actions">
                    <GuidePrice
                        guidePrice={auction.guidePrice}
                        countryId={auction.countryId}
                        currency={auction.currency}
                    />
                    <BidNowButton
                        title={auction.title}
                        biddingUrl={auction.biddingUrl}
                    />
                </div>
            </header>

            {/* Info Row */}
            <div className="al-info-row">
                <div className="al-info-item al-address">
                    <div className="al-info-icon">
                        <MapPin size={20} />
                    </div>
                    <div className="al-info-content">
                        <span className="al-info-label">Location</span>
                        {auction.street && (
                            <a
                                href={googleMapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="al-info-value"
                            >
                                {auction.street}
                            </a>
                        )}
                        <span className="al-info-secondary">{cityState}</span>
                    </div>
                </div>

                <div className="al-info-item al-date-time">
                    <div className="al-info-icon">
                        <Calendar size={20} />
                    </div>
                    <div className="al-info-content">
                        <span className="al-info-label">Date &amp; Time</span>
                        <div className="al-info-value">
                            <DateTimeRange
                                startDate={auction.date}
                                startTime={auction.beginTime}
                                endDate={auction.endDate}
                                endTime={auction.endTime}
                                countryId={auction.countryId}
                            />
                        </div>
                    </div>
                </div>

                <div className="al-info-item al-auctioneer">
                    <div className="al-info-icon">
                        <User size={20} />
                    </div>
                    <div className="al-info-content">
                        <span className="al-info-label">Auctioneer</span>
                        <Link to={auctioneerUrl} className="al-info-value">
                            {auction.auctioneerName}
                            {designations && (
                                <span className="al-designations">{designations}</span>
                            )}
                        </Link>
                        {auctioneerLocation && (
                            <span className="al-info-secondary">{auctioneerLocation}</span>
                        )}
                    </div>
                </div>
            </div>

            {/* Real Estate Details */}
            <RealEstateDetails auction={auction} />

            {/* Energy Rating (UK/Ireland) */}
            <EnergyRating
                countryId={auction.countryId}
                epcRating={auction.epcRating}
                berRating={auction.berRating}
            />

            {/* Description */}
            {auction.description && (
                <section className="al-section al-description-section">
                    <h3 className="al-section-title">Description</h3>
                    <pre className="al-description">{auction.description}</pre>
                </section>
            )}

            {/* Real Estate Characteristics */}
            <RealEstateCharacteristics auction={auction} />

            {/* UK/Ireland Property Details */}
            <PropertyDetails
                countryId={auction.countryId}
                tenure={auction.tenure}
                councilTaxBand={auction.councilTaxBand}
                groundRent={auction.groundRent}
                leaseYearsRemaining={auction.leaseYearsRemaining}
                heritageStatus={auction.heritageStatus}
            />

            {/* Directions */}
            <DirectionsSection
                directions={auction.directions}
                googleMapsUrl={googleMapsUrl}
            />

            {/* Terms */}
            <TermsSection terms={auction.terms} />

            {/* Categories */}
            <AuctionCategories categories={auction.categories} />

            {/* Action Bar */}
            <ActionBar auction={auction} auctioneerUrl={auctioneerUrl} />

            {/* Overflow Photos */}
            <PhotoGrid
                photos={overflowPhotos}
                auctionId={auctionId}
                title={auction.title}
            />
        </article>
    );
};

AuctionPage.propTypes = {
    onInitialize: PropTypes.func,
    auction: AUCTION_PROP_TYPE,
    mainPhoto: PHOTO_PROP_TYPE,
    secondaryPhotos: PropTypes.arrayOf(PHOTO_PROP_TYPE),
    overflowPhotos: PropTypes.arrayOf(PHOTO_PROP_TYPE),
};

export default AuctionPage;
